import { Autocomplete, FilterOptionsState } from '@mui/material';
import React, { useCallback } from 'react';

import { BaseInputProps, StyledTextField } from './StyledTextField';

export interface AutocompleteInputProps<T> extends BaseInputProps {
  /** Valor seleccionado */
  value: T | null;
  /** Callback cuando cambia el valor */
  onChange: (value: T | null) => void;
  /** Opciones disponibles */
  options: T[];
  /** Función para obtener el label de una opción */
  getOptionLabel: (option: T) => string;
  /** Función personalizada para filtrar opciones */
  filterOptions?: (options: T[], inputValue: string) => T[];
  /** Abrir al enfocar (default: true) */
  openOnFocus?: boolean;
  /** Nombre del campo para formularios */
  name?: string;
  /** Tamaño del input */
  size?: 'small' | 'medium';
  /** Permitir limpiar el valor */
  clearable?: boolean;
  /** Función para comparar opciones */
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  /** Loading state */
  loading?: boolean;
  /** Texto cuando no hay opciones */
  noOptionsText?: string;
}

/**
 * Características:
 * - Autocomplete de MUI con búsqueda
 * - Genérico para cualquier tipo
 * - Filtrado personalizable
 * - Estilo consistente con otros inputs
 */
export function AutocompleteInput<T>({
  value,
  onChange,
  options,
  getOptionLabel,
  filterOptions,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
  fullWidth = true,
  placeholder,
  openOnFocus = true,
  name,
  size = 'small',
  clearable = true,
  isOptionEqualToValue,
  loading,
  noOptionsText,
}: AutocompleteInputProps<T>): React.ReactElement {
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: T | null) => {
      onChange(newValue);
    },
    [onChange],
  );

  // Wrapper para filterOptions compatible con MUI
  const muiFilterOptions = filterOptions
    ? (options: T[], state: FilterOptionsState<T>) =>
        filterOptions(options, state.inputValue)
    : undefined;

  return (
    <Autocomplete
      value={value}
      onChange={handleChange}
      options={options}
      getOptionLabel={getOptionLabel}
      filterOptions={muiFilterOptions}
      disabled={disabled}
      fullWidth={fullWidth}
      openOnFocus={openOnFocus}
      disableClearable={!clearable}
      size={size}
      isOptionEqualToValue={isOptionEqualToValue}
      loading={loading}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          required={required}
          name={name}
        />
      )}
    />
  );
}
